import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { unsubscribeEmail } from "@/lib/emailService";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = searchParams.get("email");

  const handleUnsubscribe = async () => {
    if (!email) {
      setError("No email address provided");
      return;
    }

    setIsUnsubscribing(true);
    setError(null);

    try {
      const success = await unsubscribeEmail(email);

      if (success) {
        setUnsubscribed(true);
        toast({
          title: "Successfully Unsubscribed",
          description: `${email} has been removed from our newsletter list.`,
        });
      } else {
        setError("Email address not found in our newsletter list.");
      }
    } catch (error) {
      console.error("Unsubscribe error:", error);
      setError("Failed to unsubscribe. Please try again later.");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to unsubscribe. Please try again later.",
      });
    } finally {
      setIsUnsubscribing(false);
    }
  };

  if (!email) {
    return (
      <div className="min-h-screen bg-sand-beige flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-forest-green">Invalid Request</CardTitle>
            <CardDescription>
              No email address was provided for unsubscription.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (unsubscribed) {
    return (
      <div className="min-h-screen bg-sand-beige flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-forest-green">
              Successfully Unsubscribed
            </CardTitle>
            <CardDescription>
              {email} has been removed from our newsletter list.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-forest-green/70 mb-4">
              You will no longer receive newsletters from Cittasya. We're sorry
              to see you go!
            </p>
            <Button
              onClick={() => (window.location.href = "/")}
              className="bg-forest-green hover:bg-forest-green/90"
            >
              Return to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-beige flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-forest-green">
            Unsubscribe from Newsletter
          </CardTitle>
          <CardDescription className="text-black/70">
            Are you sure you want to unsubscribe {email} from our newsletter?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="text-sm text-forest-green/70">
            <p>You will no longer receive:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Weekly newsletters</li>
              <li>Product updates</li>
              <li>Special announcements</li>
            </ul>
          </div>

          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={() => (window.location.href = "/")}
              className="flex-1 border-misty-sage text-forest-green hover:bg-forest-green/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUnsubscribe}
              disabled={isUnsubscribing}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {isUnsubscribing && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isUnsubscribing ? "Unsubscribing..." : "Unsubscribe"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
